import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArticleDesk, {
    DeskRibbon,
    DeskPanel,
    DeskTitle,
    DeskNote,
    DESK_BUTTON_CLASS,
} from '../../../components/staff/articles/ArticleDesk';

const CATEGORY_NAME_TO_COLOR = {
    'frankly news': '#6d1b1b',
    'habbo news': '#c48a00',
    'chickas charity': '#c86aa0',
    'rare values': '#4f9ea4',
    'frankly guides': '#6d1b1b',
    'guides': '#2d6a1e',
    'competitions': '#6c2d82',
};

function ArticleRow({ article, onEdit }) {
    const catColor = CATEGORY_NAME_TO_COLOR[article.categoryName?.toLowerCase()] || '#6b7280';

    const formatDate = (d) => {
        if (!d) return '-';
        return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
        <tr className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
            <td className="py-3 px-3">
                <div className="flex items-center gap-2">
                    {article.draft ? (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-yellow-100 text-yellow-700 border border-yellow-300 shrink-0">
                            Draft
                        </span>
                    ) : (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-green-100 text-green-700 border border-green-300 shrink-0">
                            Live
                        </span>
                    )}
                    <span className="text-[13px] text-gray-900 line-clamp-1">{article.title}</span>
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-1 pl-[calc(10px+8px+4px)]">{article.excerpt}</p>
            </td>
            <td className="py-3 px-3 hidden sm:table-cell">
                <span
                    className="text-[11px] uppercase font-bold px-2 py-0.5 text-white whitespace-nowrap"
                    style={{ backgroundColor: catColor, boxShadow: '0 1px 0 0 rgba(0,0,0,0.2)' }}
                >
                    {article.categoryName}
                </span>
            </td>
            <td className="py-3 px-3 hidden md:table-cell text-[12px] text-gray-600 whitespace-nowrap">
                {article.author}
            </td>
            <td className="py-3 px-3 hidden lg:table-cell text-[12px] text-gray-500 whitespace-nowrap">
                {formatDate(article.publicDate)}
            </td>
            <td className="py-3 px-3">
                <button
                    onClick={() => onEdit(article.id)}
                    className="bg-maroon text-white text-[11px] uppercase font-['Jersey_10'] tracking-[1px] px-3 py-1 hover:opacity-80 transition-opacity whitespace-nowrap"
                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                >
                    Edit
                </button>
            </td>
        </tr>
    );
}

function ArticleManagement({ userData }) {
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const apiUrl = process.env.REACT_APP_API_URL;

    const loadPage = useCallback(async (page, isInitial = false) => {
        isInitial ? setLoading(true) : setPageLoading(true);
        try {
            const params = new URLSearchParams({ page, sort: 'recent' });
            const res = await fetch(`${apiUrl}admin/articles/all?${params}`, { credentials: 'include' });
            const data = await res.json();
            if (data.success) {
                setArticles(data.articles);
                setPagination(data.pagination);
            } else {
                toast.error('Failed to load articles');
            }
        } catch {
            toast.error('Failed to load articles');
        } finally {
            isInitial ? setLoading(false) : setPageLoading(false);
        }
    }, [apiUrl]);

    useEffect(() => {
        loadPage(1, true);
    }, [loadPage]);

    if (!userData || !userData.hasAdminRole) return <Navigate to="/" replace />;

    const handleEdit = (articleId) => {
        navigate(`/staff/admin/articles/edit/${articleId}`);
    };

    return (
        <ArticleDesk userData={userData}>
            <DeskRibbon>
                <DeskTitle>Article Management</DeskTitle>
                <DeskNote>
                    {pagination.total} article{pagination.total !== 1 ? 's' : ''}
                </DeskNote>

                <div className="flex-1" />

                {pagination.totalPages > 1 && (
                    <>
                        <DeskNote>Page {pagination.page} of {pagination.totalPages}</DeskNote>
                        <button
                            type="button"
                            onClick={() => loadPage(pagination.page - 1)}
                            disabled={pagination.page <= 1 || pageLoading}
                            className={`${DESK_BUTTON_CLASS} disabled:cursor-not-allowed disabled:opacity-40`}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            onClick={() => loadPage(pagination.page + 1)}
                            disabled={pagination.page >= pagination.totalPages || pageLoading}
                            className="px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.3)', backgroundColor: '#5A2929' }}
                        >
                            Next
                        </button>
                    </>
                )}
            </DeskRibbon>

            <DeskPanel padding="p-0">
                {loading ? (
                    <div className="space-y-2 p-3">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div key={i} className="h-12 animate-pulse bg-gray-100" />
                        ))}
                    </div>
                ) : articles.length === 0 ? (
                    <p className="py-10 text-center text-sm uppercase text-gray-500">No articles found</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-100">
                                    <th className="px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px]">Article</th>
                                    <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] sm:table-cell">Category</th>
                                    <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] md:table-cell">Author</th>
                                    <th className="hidden px-3 py-2 text-left font-['Jersey_10'] text-[13px] uppercase tracking-[1px] lg:table-cell">Date</th>
                                    <th className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody className={pageLoading ? 'pointer-events-none opacity-50' : ''}>
                                {articles.map(article => (
                                    <ArticleRow key={article.id} article={article} onEdit={handleEdit} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </DeskPanel>
        </ArticleDesk>
    );
}

export default ArticleManagement;
